import React from 'react';
import { useNavigate } from 'react-router-dom';
import { PATH } from 'constants/path';
import s from './s.module.less';

type Props = {
    faxMissing: boolean;
    payoutMissing: boolean;
    onSetupPayout: () => void;
};

export const ArrowSm = () => (
    <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
    >
        <line x1="5" y1="12" x2="19" y2="12" />
        <polyline points="12 5 19 12 12 19" />
    </svg>
);

export const WarnIcon = ({ color = '#D97706' }: { color?: string }) => (
    <svg width="18" height="18" viewBox="0 0 20 20" fill={color} className={s.icon} aria-hidden>
        <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.168 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 6a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 6zm0 9a1 1 0 100-2 1 1 0 000 2z"
        />
    </svg>
);

const InfoIcon = () => (
    <svg width="18" height="18" viewBox="0 0 20 20" fill="#2563EB" className={s.icon} aria-hidden>
        <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z"
        />
    </svg>
);

// 首次理赔前的两项准备提醒（原型 setupReminderBannerHTML）：fax 与 payout 共用一条 banner，
// 一项一个入口，做完一项该入口消失，两项都做完整条消失；不可关闭。
// 色调跟着 fax 走：缺 fax 时黄（payer 没有 fax 无法给 provider 入组），
// 只剩 payout 时蓝（首个 panel 上线前只是提醒，不是阻塞）。
export default function SetupReminderBanner({ faxMissing, payoutMissing, onSetupPayout }: Props) {
    const navigate = useNavigate();
    if (!faxMissing && !payoutMissing) return null;

    const gotoEfax = () => navigate(`${PATH.DASHBOARD}/${PATH.EFAX}`);
    const warning = faxMissing;

    let text: React.ReactNode;
    if (faxMissing && payoutMissing) {
        // 两项都缺时每项只给一句话，eFax 价格不重复（完整推销在 submitted 页的 fax 卡上）
        text =
            'Two things to finish before your first claim: a fax number, which payers need for authorizations, records requests, and remits, and your payout account, where reimbursements are deposited.';
    } else if (faxMissing) {
        text = (
            <>
                No fax number on file. Payers need one on file for authorizations, records requests, and remits. Use
                your own if you have one, or set up Klarity eFax for <b>$9.90/month</b> with 250 pages included.
            </>
        );
    } else {
        text = 'Add your payout account so reimbursements can be deposited as soon as your first panel goes live.';
    }

    const bothMissing = faxMissing && payoutMissing;

    return (
        <div className={`${s.banner} ${warning ? s.warning : s.info}`}>
            {warning ? <WarnIcon /> : <InfoIcon />}
            <span className={s.text}>{text}</span>
            <span className={s.actions}>
                {faxMissing && (
                    <button type="button" className={s.action} onClick={gotoEfax}>
                        {bothMissing ? 'Add fax' : 'Set up'}
                        <ArrowSm />
                    </button>
                )}
                {payoutMissing && (
                    <button type="button" className={s.action} onClick={onSetupPayout}>
                        {bothMissing ? 'Payout account' : 'Set up'}
                        <ArrowSm />
                    </button>
                )}
            </span>
        </div>
    );
}
